import { inject, Network, NetworkException } from "fw";
import { createFrom } from "fw-model";
import { LocalStorageCache } from "caching";
import { ManagerConfig } from "config/manager-config";
import { parseJwt } from "helpers/parseJwt";

import { NetworkAuthMiddleware } from "./middleware/network-auth-middleware";
import { User } from "models/user";

const tokenKeyName = "manager-bearer-token";
const tokenExpDateName = "manager-bearer-token-expiration-date";
const tokenLifespanName = "manager-bearer-token-lifespan";

@inject
export class Manager {
    private _tokenLifespan: number = 0;

    constructor(
        private network: Network,
        private cache: LocalStorageCache,
        private networkAuth: NetworkAuthMiddleware,
        private config: ManagerConfig
    ) {
        this.network.addMiddleware(NetworkAuthMiddleware);

        const tokenLifespan = this.cache.get<number>(tokenLifespanName);
        if (tokenLifespan) this.setTokenLifespan(tokenLifespan);

        const token = this.cache.get<string>(tokenKeyName);
        if (token) this.setToken(token);
    }

    public setToken(token: string) {
        this.networkAuth.token = token;
        this.cache.set(tokenKeyName, token);
        this.cache.set(tokenExpDateName, new Date(parseJwt(token).exp * 1000));
    }

    public getTokenExpDate(): string {
        return this.cache.get(tokenExpDateName) ?? "";
    }

    public get hasToken() {
        return this.networkAuth.token != null;
    }

    public get token() {
        return this.networkAuth.token;
    }

    public deleteToken() {
        this.networkAuth.token = null;
        this.cache.remove(tokenKeyName);
        this.cache.remove(tokenExpDateName);
    }

    public async currentUser() {
        const res = await this.get("user/me");
        return createFrom(User, res.body);
    }

    public setTokenLifespan(lifespan: number) {
        this._tokenLifespan = lifespan;
        if (lifespan > 0) this.cache.set(tokenLifespanName, lifespan);
        else this.cache.remove(tokenLifespanName);
    }

    public get tokenLifespan() {
        return this._tokenLifespan;
    }

    public get<T>(url: string, params?: any, baseUrl = this.config.apiUrl) {
        return this.network.get<T>(baseUrl + url, params);
    }

    public post<T>(url: string, data: any, params?: any, baseUrl = this.config.apiUrl) {
        return this.network.post<T>(baseUrl + url, data, params);
    }

    public put<T>(url: string, data: any, params?: any, baseUrl = this.config.apiUrl) {
        return this.network.put<T>(baseUrl + url, data, params);
    }

    public patch<T>(url: string, data: any, params?: any, baseUrl = this.config.apiUrl) {
        return this.network.patch<T>(baseUrl + url, data, params);
    }

    public delete<T>(url: string, params?: any, baseUrl = this.config.apiUrl) {
        return this.network.delete<T>(baseUrl + url, params);
    }

    public async getWithSignal<T>(url: string, params?: any, signal?: AbortSignal, baseUrl = this.config.apiUrl) {
        // create a promise that rejects if the signal is aborted
        if (signal?.aborted) {
            throw new DOMException("Request aborted", "AbortError");
        }

        const requestPromise = this.network.get<T>(baseUrl + url, params);

        if (!signal) {
            return requestPromise;
        }

        // race the request against the abort signal
        return new Promise<{ headers: any; body: T }>((resolve, reject) => {
            const abortListener = () => {
                reject(new DOMException("Request aborted", "AbortError"));
            };

            signal.addEventListener("abort", abortListener, { once: true });

            requestPromise
                .then(resolve)
                .catch(reject)
                .finally(() => {
                    signal.removeEventListener("abort", abortListener);
                });
        });
    }

    public async postWithSignal<T>(url: string, data: any, params?: any, signal?: AbortSignal, baseUrl = this.config.apiUrl) {
        // create a promise that rejects if the signal is aborted
        if (signal?.aborted) {
            throw new DOMException("Request aborted", "AbortError");
        }

        const requestPromise = this.network.post<T>(baseUrl + url, data, params);

        if (!signal) {
            return requestPromise;
        }

        // race the request against the abort signal
        return new Promise<{ headers: any; body: T }>((resolve, reject) => {
            const abortListener = () => {
                reject(new DOMException("Request aborted", "AbortError"));
            };

            signal.addEventListener("abort", abortListener, { once: true });

            requestPromise
                .then(resolve)
                .catch(reject)
                .finally(() => {
                    signal.removeEventListener("abort", abortListener);
                });
        });
    }
}

const getParams = (q: string = null, f: string = null, aggs: string = null, sort: string = null, page = 1, limit = 10) => {
    const params: any = {
        page,
        limit: limit == null ? 10 : limit
    };

    if (sort != null) params.sort = sort;
    if (q != null) params.q = q;
    if (f != null) params.f = f;
    if (aggs != null) params.aggs = aggs;

    return params;
};

export interface ValidationResult {
    data: any;
    validation_result: { message: string; name: string; type: string }[];
}

export interface IUsageProfileResult<T> {
    Message: string;
    Data: {
        UsageProfiles: T[];
    };
}

export function isValidationResult(d): d is NetworkException<ValidationResult> {
    return d.statusCode == 400 && d.result && d.result.validation_result;
}

export interface IListResults<T> {
    results: T[];
    total: number;
    aggregations?: { [key: string]: IAggregationResult };
}

export interface ICountResult {
    total?: number;
    aggregations?: { [key: string]: IAggregationResult };
}

export interface IAggregationResult {
    total?: number;
    value?: number;
    items?: IBucketResult[];
    aggregations?: { [key: string]: IAggregationResult };
}

export interface IBucketResult {
    key: string | number | boolean | Date;
    key_as_string?: string;
    value?: number;
    total?: number;
    aggregations?: { [key: string]: IAggregationResult };
}

export class EntitySelection {
    ids: string[] = [];
    excludedIds: string[] = [];
    search: string = null;
    filter: string = null;
}
